import React from 'react';
import PropTypes from 'prop-types';
import { space, colors, fontSizes } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Flex, Box, Divider, Text, Link, Image, Grid
} from 'components/ui';

const ORDERS_URL = '/profile/MyAccount/Orders';

/**
 * Displays a summary of auto-replenishment subscriptions with status counts and benefit metrics
 */
function AutoReplenishmentSummary({ localization, allSubscriptions, biPoints, isAutoReplenishEmptyHubEnabled }) {
    const statuses = [
        { title: localization.activeText, value: allSubscriptions.active ?? 0 },
        { title: localization.pausedText, value: allSubscriptions.paused ?? 0 },
        ...(isAutoReplenishEmptyHubEnabled ? [{ title: localization.canceledText, value: allSubscriptions.cancelled ?? 0 }] : [])
    ];

    const items = [
        {
            title: localization.biPointsEarned,
            src: '/img/ufe/icons/points.svg',
            value: `${biPoints} ${localization.points}`
        },
        {
            title: localization.totalSaving,
            src: '/img/ufe/icons/auto-replenish.svg',
            value: allSubscriptions.totalAggregateDiscount
        }
    ];

    return (
        <Box css={styles.container}>
            <Grid css={styles.content}>
                <Box>
                    <Text
                        is='p'
                        css={styles.title}
                    >
                        {localization.autoReplenishSummary}
                    </Text>
                    <Box css={styles.status}>
                        {statuses.map((status, index) => (
                            <React.Fragment key={status.title}>
                                {index > 0 && <span css={styles.separator}>|</span>}
                                {`${status.title} (${status.value})`}
                            </React.Fragment>
                        ))}
                    </Box>
                </Box>
                {!isAutoReplenishEmptyHubEnabled && (
                    <Flex>
                        <Link
                            css={{ ...styles.link, ...styles.vAlignMiddle }}
                            href={ORDERS_URL}
                            children={localization.viewShipments}
                        />
                    </Flex>
                )}
            </Grid>
            <Divider />
            <Grid css={styles.content}>
                <Box css={styles.items}>
                    {items.map(item => (
                        <Box
                            css={styles.item}
                            key={item.title}
                        >
                            <Image
                                css={styles.itemIcon}
                                size={25}
                                src={item.src}
                            />
                            <Text is='p'>{item.title}</Text>
                            <Text
                                is='span'
                                css={styles.itemValue}
                            >
                                <strong>{item.value}</strong>
                            </Text>
                        </Box>
                    ))}
                </Box>
            </Grid>

            {isAutoReplenishEmptyHubEnabled && (
                <>
                    <Divider />
                    <Flex>
                        <Link
                            css={{ ...styles.link, ...styles.hAlignLeft }}
                            href={ORDERS_URL}
                            children={localization.viewShipments}
                        />
                    </Flex>
                </>
            )}
        </Box>
    );
}

const styles = {
    container: {
        backgroundColor: colors.nearWhite,
        padding: `${space[1]}px ${space[4]}px 0 ${space[4]}px`,
        marginTop: space[4],
        borderRadius: space[1]
    },
    content: {
        gap: 0,
        gridTemplateColumns: '1fr auto',
        padding: `${space[3]}px 0`
    },
    title: {
        marginBottom: space[1]
    },
    status: {
        fontSize: fontSizes.sm
    },
    separator: {
        margin: `0 ${space[2]}px`,
        color: colors.midGray
    },
    items: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[3]
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        fontSize: fontSizes.sm
    },
    itemIcon: {
        verticalAlign: 'middle',
        marginRight: space[3],
        flexShrink: 0
    },
    itemValue: {
        textAlign: 'right',
        marginLeft: 'auto',
        wordWrap: 'normal'
    },
    link: {
        color: colors.blue,
        margin: `${space[3]}px 0`
    },
    vAlignMiddle: {
        margin: 'auto'
    },
    hAlignLeft: {
        margin: `${space[3]}px 0`
    }
};

AutoReplenishmentSummary.propTypes = {
    localization: PropTypes.object.isRequired,
    allSubscriptions: PropTypes.shape({
        active: PropTypes.number,
        paused: PropTypes.number,
        cancelled: PropTypes.number,
        totalAggregateDiscount: PropTypes.string
    }).isRequired,
    biPoints: PropTypes.number.isRequired,
    isAutoReplenishEmptyHubEnabled: PropTypes.bool
};

export default wrapFunctionalComponent(AutoReplenishmentSummary, 'AutoReplenishmentSummary');
